// ============================================================
// FILE: @components/modal-shell/modal-shell.component.ts
// Reusable modal scaffold (backdrop + header + body + footer slots).
//
// Ported from EzovionOPDFrontend (Angular 20) shared/components/
// modal-shell/modal-shell.component.ts -- standalone component
// converted to a plain NgModule-declared component (registered in
// ComponentsModule so both Device Integration and the later-ported
// Triage feature can reuse it without duplication), template `@if`
// converted to `*ngIf`. OnPush change detection dropped to match this
// target app's typical non-OnPush convention (Angular 9 has no Signals
// API to pair with it here).
//
// Usage:
//   <app-modal-shell *ngIf="open" (close)="onClose()" [title]="..." [subtitle]="...">
//     <div body>...</div>
//     <div footer>...</div>
//   </app-modal-shell>
// ============================================================
import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'app-modal-shell',
  template: `
    <div class="ms-backdrop" (click)="onBackdropClick($event)" role="presentation">
      <div class="ms-box" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">
        <div class="ms-header">
          <div class="ms-text">
            <div class="ms-title">{{ title }}</div>
            <div class="ms-sub" *ngIf="subtitle">{{ subtitle }}</div>
          </div>
          <button class="ms-close" type="button" aria-label="Close" (click)="close.emit()">✕</button>
        </div>
        <div class="ms-body"><ng-content select="[body]"></ng-content></div>
        <div class="ms-footer"><ng-content select="[footer]"></ng-content></div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      --surface: #ffffff;
      --border: rgba(0,0,0,0.08);
      --text: #1a1916;
      --text2: #6b6a65;
      --text3: #9b9a96;
      --radius: 10px;
      --shadow-lg: 0 12px 40px rgba(0,0,0,0.12);
    }
    .ms-backdrop {
      position: fixed; inset: 0;
      background: rgba(0,0,0,.45);
      display: flex; align-items: center; justify-content: center;
      z-index: 1000; padding: 16px;
    }
    .ms-box {
      background: var(--surface);
      border-radius: var(--radius);
      box-shadow: var(--shadow-lg);
      width: 100%; max-width: 560px;
      max-height: 90vh; overflow: hidden;
      display: flex; flex-direction: column;
    }
    .ms-header {
      display: flex; align-items: flex-start; justify-content: space-between;
      gap: 12px; padding: 16px 18px;
      border-bottom: 1px solid var(--border);
    }
    .ms-title { font-size: 15px; font-weight: 500; color: var(--text); }
    .ms-sub   { font-size: 12px; color: var(--text2); margin-top: 2px; }
    .ms-close {
      background: none; border: 0; cursor: pointer;
      color: var(--text3); font-size: 16px; padding: 4px 8px;
    }
    .ms-close:hover { color: var(--text); }
    .ms-body   { padding: 16px 18px; overflow-y: auto; flex: 1; }
    .ms-footer { padding: 12px 18px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 8px; }
    .ms-footer:empty { display: none; }
  `],
})
export class ModalShellComponent {
  @Input() title = '';
  @Input() subtitle?: string;
  @Input() closeOnBackdrop = true;
  @Output() close = new EventEmitter<void>();

  onBackdropClick(_: Event): void {
    if (this.closeOnBackdrop) this.close.emit();
  }
}
